import { Box, Container, IconButton, Stack, useMediaQuery } from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import { SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import SwiperCarousel from '../../shared/SwiperCarousel';
import useMaxHeightCard from '../../eir-offer-card/offer-cart.util';
import styles from './styles';
import RootContext from '../../../../util/root.context';
import EirOfferCard from '../../eir-offer-card/EirOfferCard';
import {
  addPrepayAddOn,
  getPrepayAddons,
  onLoadPrepayAddons,
  removePrepayAddOn,
} from './handsetDetails.util';
import EirSingleOfferCard from '../../eir-offer-card/EirSingleOfferCard';

/**
 * @name PrepayStandaloneOfferCardContainer - jsx component for compatible addons
 * @param {object} cmsData - Strapi data
 * @param {Array} handsetCode - Handset Code returned from getHandset
 * @returns {JSX.Element} - Returns compatible addons component
 */
const PrepayStandaloneOfferCardContainer = ({
  handsetCode,
  loadingHandset,
  addons,
  setAddons,
  setShowNext,
}) => {
  const theme = useTheme();
  const style = styles(theme);
  const graphql = useApolloClient();
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const md = useMediaQuery(theme.breakpoints.down('md'));
  const xs = useMediaQuery(theme.breakpoints.down('sm'));
  const [leftArrowDisabled, setLeftArrowDisabled] = useState(true);
  const [RightArrowDisabled, setRightArrowDisabled] = useState(false);
  const { state: rootState, dispatch } = useContext(RootContext);
  const [showLoader, setShowLoader] = useState('');
  const [offerSelected, setOfferSelected] = useState(null);
  const useMaxHeight = useMaxHeightCard();
  const swiperRef = useRef(null);

  const handleLeftClick = useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.swiper.slidePrev();
  }, []);

  const handleRightClick = useCallback(() => {
    if (!swiperRef?.current) return;
    swiperRef.current.swiper.slideNext();
  }, []);

  const checkArrowsStatus = () => {
    if (swiperRef?.current?.swiper?.isBeginning) {
      setLeftArrowDisabled(true);
    }
    if (swiperRef?.current?.swiper?.isEnd) {
      setRightArrowDisabled(true);
    }
    if (!swiperRef?.current?.swiper?.isBeginning) {
      setLeftArrowDisabled(false);
    }
    if (!swiperRef?.current?.swiper?.isEnd) {
      setRightArrowDisabled(false);
    }
  };

  const handleKeydown = e => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.target.click();
    }
  };

  const updatePaginationDots = swiper => {
    const paginationDots = swiper.el?.querySelectorAll('.swiper-pagination-bullet');
    if (!paginationDots || !paginationDots.length) return;

    const activeIndex = swiper.realIndex ?? swiper.activeIndex ?? 0;

    paginationDots.forEach((dot, i) => {
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');

      dot.removeEventListener('keydown', handleKeydown);
      dot.addEventListener('keydown', handleKeydown);

      if (i === activeIndex) {
        dot.setAttribute('aria-label', 'Current plan');
      } else if (i < activeIndex) {
        dot.setAttribute('aria-label', 'Previous plan');
      } else {
        dot.setAttribute('aria-label', 'Next plan');
      }
    });
  };

  useEffect(() => {
    checkArrowsStatus();
  }, [addons]);

  /**
   * @function getAddons
   * @description - Function to load prepay addons
   */
  const getAddons = async () => {
    const data = await getPrepayAddons({ handsetCode });
    setAddons(data);
    setLoading(false);
  };

  useEffect(() => {
    getAddons({ handsetCode });
  }, [handsetCode]);

  useEffect(() => {
    if (!loadingHandset) {
      onLoadPrepayAddons(rootState, setOfferSelected, dispatch, graphql);
    }
  }, [rootState]);

  return (
    <Container id='offerCardData'>
      {!loading && addons?.length > 0 ? (
        <Box
          sx={{
            ...(addons?.length === 1 ? styles(theme).singleOfferCard : styles(theme).offerCards),
          }}
          id='offerCardData'
        >
          {!md && !leftArrowDisabled && addons?.length > 3 && (
            <IconButton
              size='small'
              sx={styles(theme).prevArrow}
              onClick={handleLeftClick}
              disableRipple
              disableTouchRipple
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
          )}
          <SwiperCarousel
            settings={{
              className: 'content-carousel-mobile',
              pagination: { enable: true, clickable: true },
              modules: [Pagination],
              initialSlide: 0,
              updateOnWindowResize: true,
              slidesPerView: 'auto',
              slidesPerGroup: addons?.length > 5 && !md ? 3 : 1,
              onSlideChange: swiper => {
                checkArrowsStatus();
                updatePaginationDots(swiper);
              },
              breakpoints: {
                320: {
                  slidesPerView: 1,
                },
                600: {
                  slidesPerView: 2,
                },
                1200: {
                  slidesPerView: 3,
                },
              },
              onAfterInit: swiper => {
                setTimeout(() => {
                  updatePaginationDots(swiper);
                }, 100);
              },
              onPaginationUpdate: swiper => {
                updatePaginationDots(swiper);
              },
              onBreakpoint: swiper => {
                updatePaginationDots(swiper);
              },
            }}
            sliderRef={swiperRef}
            style={{
              ...style.offerSlider.swiper,
              ...(addons?.length < 3 && style.offerSlider.centerSwiper),
            }}
          >
            {addons &&
              addons?.length > 0 &&
              addons?.map(
                (offer, index) =>
                  ((addons?.length > 0 && xs) || addons?.length > 1) && (
                    <SwiperSlide key={index}>
                      <EirOfferCard
                        slideNumber={index}
                        {...offer}
                        headerImage={offer?.image}
                        afterPrice={offer.afterPrice}
                        afterPricePosition={offer.afterPricePosition?.[0]}
                        moreInfo={offer.moreInfo}
                        index={index}
                        offer={offer}
                        selectButton={{
                          title: 'Select Plan',
                          action: () =>
                            offer.code === offerSelected
                              ? removePrepayAddOn(
                                  graphql,
                                  offer.code,
                                  rootState,
                                  setShowLoader,
                                  setOfferSelected,
                                  dispatch,
                                  handsetCode
                                )
                              : addPrepayAddOn(
                                  graphql,
                                  offer.code,
                                  rootState,
                                  setShowLoader,
                                  setOfferSelected,
                                  dispatch,
                                  handsetCode,
                                  setShowNext,
                                  router
                                ),
                        }}
                        color='primary'
                        isMobile
                        useMaxHeight={useMaxHeight}
                        isPrepayMobile
                        code={offer.code}
                        showLoader={showLoader}
                        offerSelected={offer.code === offerSelected}
                      />
                    </SwiperSlide>
                  )
              )}
          </SwiperCarousel>
          {addons &&
            addons?.length > 0 &&
            addons?.length === 1 &&
            !xs &&
            addons?.map((offer, index) => (
              <EirSingleOfferCard
                {...offer}
                singleTariffHeaderImage={offer.singleTariffImage}
                index={index}
                offer={offer}
                selectButton={{
                  title: 'Select Plan',
                  action: () =>
                    offer.code === offerSelected
                      ? removePrepayAddOn(
                          graphql,
                          offer.code,
                          rootState,
                          setShowLoader,
                          setOfferSelected,
                          dispatch,
                          handsetCode
                        )
                      : addPrepayAddOn(
                          graphql,
                          offer.code,
                          rootState,
                          setShowLoader,
                          setOfferSelected,
                          dispatch,
                          handsetCode,
                          setShowNext,
                          router
                        ),
                }}
                color='primary'
                code={offer.code}
                showLoader={showLoader}
                offerSelected={offer.code === offerSelected}
              />
            ))}
          {!md && !RightArrowDisabled && addons?.length > 3 && (
            <IconButton
              size='small'
              sx={styles(theme).nextArrow}
              onClick={handleRightClick}
              disableRipple
              disableTouchRipple
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          )}
        </Box>
      ) : (
        <Stack spacing={3} direction='row' justifyContent={{ md: 'center' }}>
          {Array(2)
            .fill()
            .map((_, index) => (
              <EirOfferCard loading index={index} />
            ))}
        </Stack>
      )}
    </Container>
  );
};

PrepayStandaloneOfferCardContainer.displayName = 'PrepayStandaloneOfferCardContainer';
export default PrepayStandaloneOfferCardContainer;
